import type { MouseEvent, ReactElement, ReactNode } from 'react';
import { Icons } from '@/shared/constants/icons';

export interface RiskBannerAction {
  label: string;
  onClick?: () => void;
  /** An in-app or outside link instead of a click handler. */
  href?: string;
  busy?: boolean;
  busyLabel?: string;
  /** The accessible name when the label alone is not enough ("Reconnect LinkedIn"). */
  ariaLabel?: string;
  /** Not available to this person (a role, the free Starter plan); `title` says why. */
  disabled?: boolean;
  title?: string;
}

interface RiskBannerProps {
  /** `warn`: will fail unless someone acts. `bad`: will not go out as it is. */
  tone?: 'warn' | 'bad';
  /** The one sentence. */
  message: ReactNode;
  /** Optional second line (`--ink-2`): when, or what else is wrong. */
  detail?: ReactNode;
  action?: RiskBannerAction;
  /** Tighter padding and type, for inside a card. */
  compact?: boolean;
  className?: string;
}

/** Clicks inside a card that opens something else stay here. */
const stop = (e: MouseEvent): void => e.stopPropagation();

/**
 * A post or a brand at risk (brief §1.6): a full-width card on `--warn-soft`
 * (`--bad-soft` when it will not go out), a 3px bar on the start edge, an
 * icon, one sentence and one action. The bar is a logical border, so it sits
 * on the right in Arabic and Urdu.
 */
export function RiskBanner({ tone = 'warn', message, detail, action, compact = false, className = '' }: RiskBannerProps): ReactElement {
  const color = tone === 'bad' ? 'var(--bad)' : 'var(--warn)';
  const Icon = tone === 'bad' ? Icons.alert : Icons.warning;
  return (
    <div
      role="status"
      data-testid="risk-banner"
      data-tone={tone}
      className={`flex items-start gap-2.5 rounded-[var(--r-md)] ${compact ? 'px-3 py-2' : 'px-4 py-3'} ${className}`}
      style={{ background: tone === 'bad' ? 'var(--bad-soft)' : 'var(--warn-soft)', borderInlineStart: `3px solid ${color}` }}
    >
      <Icon size={compact ? 14 : 16} className="mt-0.5 shrink-0" style={{ color }} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className={`m-0 font-semibold text-[var(--ink)] ${compact ? 'text-ui' : 'text-body'}`}>{message}</p>
        {detail && <div className={`m-0 mt-0.5 text-[var(--ink-2)] ${compact ? 'text-meta' : 'text-ui'}`}>{detail}</div>}
      </div>
      {action && (action.href ? (
        <a href={action.href} onClick={stop} className="btn btn-secondary btn-sm shrink-0 max-sm:min-h-11 inline-flex">
          {action.label}
        </a>
      ) : (
        <button
          type="button"
          onClick={(e) => { stop(e); action.onClick?.(); }}
          disabled={action.busy || action.disabled}
          aria-busy={action.busy || undefined}
          aria-label={action.ariaLabel}
          title={action.title}
          className="btn btn-secondary btn-sm shrink-0 max-sm:min-h-11"
        >
          {action.busy ? (action.busyLabel ?? action.label) : action.label}
        </button>
      ))}
    </div>
  );
}
